"use client";

import { useCallback, useRef, useEffect } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { useStore, startStaleDetection, stopStaleDetection } from "../store";
import { useWorkSessionsMap, useActiveWorkSessions } from "../store/selectors";
import type { WorkUpdateEntry, WorkSession } from "../store/types";

export type { WorkUpdateEntry, WorkSession } from "../store/types";

export function useWork(channelId: string | null) {
  const { token } = useAuth();
  const channelRef = useRef(channelId);
  const tokenRef = useRef(token);
  channelRef.current = channelId;
  tokenRef.current = token;

  // Start/stop stale detection
  useEffect(() => {
    startStaleDetection();
    return () => stopStaleDetection();
  }, []);

  const initFromEvents = useCallback((events: any[]) => {
    useStore.getState().initWorkFromEvents(events, channelRef.current);
  }, []);

  const handleWorkEvent = useCallback((data: any) => {
    if (data.type === "work_update" && data.channelId === channelRef.current) {
      useStore.getState().onWorkUpdate(data);
    }
    if (data.type === "work_complete" && data.channelId === channelRef.current) {
      useStore.getState().onWorkComplete(data);
    }
  }, []);

  const loadUpdates = useCallback(async (sessionId: string) => {
    const t = tokenRef.current;
    const ch = channelRef.current;
    if (!t || !ch) return;

    try {
      const data = await apiFetch<WorkUpdateEntry[]>(
        `/channels/${ch}/work/${sessionId}/updates`,
        t
      );
      if (channelRef.current !== ch) return;
      useStore.getState().setWorkUpdates(sessionId, data);
    } catch (err) {
      console.error("Failed to load work updates:", err);
    }
  }, []);

  const resetWork = useCallback(() => {
    useStore.getState().resetWork();
  }, []);

  const getSession = useCallback(
    (sessionId: string): WorkSession | undefined => {
      return useStore.getState().work.bySessionId[sessionId];
    },
    []
  );

  // Derive arrays from store
  const allSessionsMap = useWorkSessionsMap();
  const allSessions = Array.from(allSessionsMap.values());
  const activeSessions = useActiveWorkSessions(channelId);

  return {
    sessions: allSessions,
    activeSessions,
    getSession,
    handleWorkEvent,
    initFromEvents,
    loadUpdates,
    resetWork,
  };
}
